import useEmblaCarousel from "embla-carousel-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import useDevice from "@/lib/hooks/useDevice";
import getIndexStyles from "@/lib/utils/getIndexStyles";
import ArrowSvg from "@/public/svg/Arrow.svg";
import type { IResolvedProductCard } from "@/types/product";

import Animated from "../Animation/Animated";
import ProductCard from "../Product/ProductCard";
import styles from "./ProductList.module.scss";

interface ProductListProps {
  products: IResolvedProductCard[];
  title: string;
  link?: string;
}

export const ProductList = ({ products, title, link }: ProductListProps) => {
  const t = useTranslations();
  const { mobile } = useDevice();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    mobile
      ? undefined
      : {
          dragFree: true,
          containScroll: "trimSnaps",
        }
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();

    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
    emblaApi.on("resize", onSelect);

    const timer = setTimeout(onSelect, 100);

    return () => {
      clearTimeout(timer);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
      emblaApi.off("resize", onSelect);
    };
  }, [emblaApi, onSelect]);

  const showArrows = products.length > 3;

  return (
    <div className={styles.container}>
      <Animated>
        {link ? (
          <Link href={link} className={styles.title}>
            {title}
          </Link>
        ) : (
          title && <h2 className={styles.title}>{title}</h2>
        )}
      </Animated>

      <div className={styles.carouselWrapper}>
        <div
          className={styles.emblaViewport}
          ref={mobile ? undefined : emblaRef}
        >
          <div className={styles.cardList}>
            {products.map((product, i) => (
              <div
                className={`${styles.productCard} ${getIndexStyles(i, styles)}`}
                key={product?.id}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>

        {showArrows && (
          <div className={styles.navigationContainer}>
            <button
              type="button"
              className={`${styles.navButton} ${
                !canScrollPrev ? styles.disabled : ""
              }`}
              disabled={!canScrollPrev}
              onClick={scrollPrev}
              aria-label="Previous products"
            >
              <ArrowSvg />
              {t("Previous")}
            </button>
            <button
              type="button"
              className={`${styles.navButton} ${
                !canScrollNext ? styles.disabled : ""
              }`}
              disabled={!canScrollNext}
              onClick={scrollNext}
              aria-label="Next products"
            >
              {t("Next")}
              <ArrowSvg />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
